import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Aviso, CampoDeTexto } from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import { emBr } from '../pontual/comum';
import type { LeituraDaRegra } from '../pontual/tipos';

/**
 * A leitura da forma de pagamento enquanto se escreve — a mesma do Pontual
 * (RN-700): "30 dias após a transferência", "todo dia 15 do mês seguinte".
 * Só pergunta com algo escrito; em branco é o padrão, sem leitura.
 */
export function useLeituraDaFormaDePagamento(
  texto: string,
  parcelas: number,
): UseQueryResult<LeituraDaRegra> {
  const limpo = texto.trim();
  return useQuery({
    // A prévia só mostra os primeiros vencimentos: o limite do Pontual basta.
    queryKey: ['passivo-forma-de-pagamento', limpo, Math.min(60, Math.max(1, parcelas))],
    queryFn: () =>
      requisicaoAutenticada<LeituraDaRegra>('/pontual/regra/leitura', {
        metodo: 'POST',
        corpo: {
          texto: limpo,
          parcelas: Math.min(60, Math.max(1, parcelas)),
          // A transferência ainda não aconteceu: hoje só ilustra o intervalo.
          dataTransferencia: new Date().toISOString().slice(0, 10),
        },
      }),
    enabled: limpo.length > 2,
  });
}

/** Em branco, ou escrita e entendida — o que o servidor aceita. */
export function formaDePagamentoValida(
  texto: string,
  leitura: UseQueryResult<LeituraDaRegra>,
): boolean {
  return texto.trim() === '' || leitura.data?.reconhecida === true;
}

/**
 * O campo "Forma de pagamento" do contrato de Passivo, com o que o sistema
 * entendeu embaixo. Com a forma descrita, os vencimentos contam da
 * transferência do crédito (a 1ª etapa do checklist) e as parcelas nascem
 * quando ela for marcada; em branco, o padrão de sempre.
 */
export function CampoDaFormaDePagamento(props: {
  readonly valor: string;
  readonly aoMudar: (texto: string) => void;
  readonly leitura: UseQueryResult<LeituraDaRegra>;
}): JSX.Element {
  const escrito = props.valor.trim().length > 2;
  const l = props.leitura.data;

  return (
    <div className="flex flex-col gap-3">
      <CampoDeTexto
        rotulo="Forma de pagamento (opcional)"
        rows={2}
        ajuda='Como o cliente paga, contado da transferência do crédito: "30 dias após a transferência", "todo dia 15 do mês seguinte", "à vista". Em branco: parcelas mensais no dia do vencimento, a partir do mês seguinte à ativação.'
        value={props.valor}
        onChange={(e) => props.aoMudar(e.target.value)}
      />
      {escrito && l !== undefined && (
        <Aviso
          tom={!l.reconhecida || l.confianca === 'baixa' ? 'atencao' : 'positivo'}
          titulo={l.reconhecida ? 'Entendi assim' : 'Não entendi a forma de pagamento'}
        >
          {l.reconhecida ? (
            <>
              <p>{l.descricao}</p>
              <p className="mt-1 text-xs text-conteudo-secundario">
                As parcelas nascem quando a transferência do crédito for marcada no checklist,
                contadas da data dela — o dia do vencimento acima deixa de valer.
                {l.vencimentos.length > 0 &&
                  ` Se a transferência fosse hoje, as primeiras venceriam em ${l.vencimentos
                    .slice(0, 3)
                    .map(emBr)
                    .join(', ')}.`}
              </p>
            </>
          ) : (
            <p>
              Reescreva numa das formas conhecidas — "à vista", "em até N dias após a
              transferência", "dia N do mês seguinte" — ou deixe em branco para o padrão.
            </p>
          )}
        </Aviso>
      )}
    </div>
  );
}
